/**
 * components/ui/SchoolCrest.tsx
 * A clean vector crest evoking the LKC shield (royal blue + gold,
 * open book + motto banner). Used on login, sidebar, and reports so
 * the system reads as unmistakably Livingstone Kolobeng College's.
 *
 * This is an original mark inspired by the school's blue/gold crest
 * identity — not a copy of the official logo file. The real logo
 * asset can be dropped in at /public/lkc-logo.png and swapped in
 * later without touching layout.
 */
import { SCHOOL } from '@/lib/school.config';

interface Props {
  size?: number;
  showMotto?: boolean;
  className?: string;
}

export default function SchoolCrest({
  size = 56,
  showMotto = false,
  className = '',
}: Props) {
  const h = showMotto ? size * 1.28 : size;
  return (
    <svg
      width={size}
      height={h}
      viewBox="0 0 100 128"
      className={className}
      role="img"
      aria-label={`${SCHOOL.name} crest`}
    >
      <defs>
        <linearGradient id="lkcShield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2d5295" />
          <stop offset="100%" stopColor="#142c52" />
        </linearGradient>
        <linearGradient id="lkcGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f0c850" />
          <stop offset="100%" stopColor="#d4a92c" />
        </linearGradient>
      </defs>

      {/* Shield */}
      <path
        d="M50 4 L92 16 V58 C92 84 73 100 50 110 C27 100 8 84 8 58 V16 Z"
        fill="url(#lkcShield)"
        stroke="url(#lkcGold)"
        strokeWidth="3"
      />

      {/* Inner gold border */}
      <path
        d="M50 14 L84 23 V57 C84 78 68 91 50 99 C32 91 16 78 16 57 V23 Z"
        fill="none"
        stroke="#d4a92c"
        strokeWidth="1.4"
        opacity="0.7"
      />

      {/* Open book */}
      <g transform="translate(50 50)">
        <path
          d="M-22 -6 C-14 -12 -4 -12 0 -7 C4 -12 14 -12 22 -6 L22 12 C14 6 4 6 0 11 C-4 6 -14 6 -22 12 Z"
          fill="#f7f8fb"
        />
        <path d="M0 -7 L0 11" stroke="#142c52" strokeWidth="1.6" />
        <path
          d="M-17 -3 H-4 M-17 1 H-4 M-17 5 H-4 M4 -3 H17 M4 1 H17 M4 5 H17"
          stroke="#9fb0cf"
          strokeWidth="1"
        />
      </g>

      {/* Rising sun rays above book */}
      <g transform="translate(50 28)" stroke="#f0c850" strokeWidth="1.6">
        <line x1="0" y1="-9" x2="0" y2="-3" />
        <line x1="-7" y1="-7" x2="-3" y2="-2" />
        <line x1="7" y1="-7" x2="3" y2="-2" />
        <line x1="-11" y1="-2" x2="-5" y2="0" />
        <line x1="11" y1="-2" x2="5" y2="0" />
      </g>

      {/* LKC monogram */}
      <text
        x="50"
        y="86"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="13"
        fontWeight="800"
        fill="#f0c850"
        letterSpacing="1.5"
      >
        LKC
      </text>

      {showMotto && (
        <>
          <path
            d="M12 116 Q50 108 88 116 L82 124 Q50 118 18 124 Z"
            fill="url(#lkcGold)"
          />
          <text
            x="50"
            y="122"
            textAnchor="middle"
            fontFamily="Inter, sans-serif"
            fontSize="7.5"
            fontWeight="700"
            fill="#142c52"
            letterSpacing="0.5"
          >
            {SCHOOL.motto.toUpperCase()}
          </text>
        </>
      )}
    </svg>
  );
}
